import { useEffect, useMemo, useState } from "react";
import { collection, doc, onSnapshot, orderBy, query } from "firebase/firestore";
import { paths } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Label, cn } from "@clevis/shared-ui";
import { db } from "../../lib/firebase";
import { call } from "../../lib/callFunction";
import { useAuth } from "../../hooks/useAuth";
import { Link } from "wouter";

interface ImportJob {
  id: string;
  status: "running" | "done" | "failed";
  incremental?: boolean;
  progress: Record<string, { imported: number; created?: number; updated?: number; skipped?: number }>;
  error: string | null;
}

interface PickRow {
  id: number;
  label: string;
  sub: string;
}

const ENTITY_LABELS: Record<string, string> = {
  customers: "Customers",
  items: "Pricebook items",
  jobs: "Jobs",
  users: "Team members",
  team: "Team invites",
  estimates: "Estimates",
  invoices: "Invoices",
};

// include-key ↔ FieldPulse list entity (team invites come from FP users)
const IMPORT_KEYS = ["customers", "items", "jobs", "estimates", "invoices", "team"] as const;
type ImportKey = (typeof IMPORT_KEYS)[number];
const LIST_ENTITY: Record<ImportKey, string> = {
  customers: "customers",
  items: "items",
  jobs: "jobs",
  estimates: "estimates",
  invoices: "invoices",
  team: "users",
};
const KEY_LABEL: Record<ImportKey, string> = {
  customers: "Customers (with builder/community structure)",
  items: "Items → Pricebook",
  jobs: "Jobs (completed ones come in as history)",
  estimates: "Estimates (keep their FieldPulse numbers)",
  invoices: "Invoices & payments (keep their FieldPulse numbers)",
  team: "Team members (as invites — nobody is emailed)",
};

const fmtWhen = (ms: number) =>
  new Date(ms).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

// Connect FieldPulse → counts persist across visits → pick records per
// entity in a popup → import / re-sync with live progress.
export function MigrationPage() {
  const { tenantId } = useAuth();
  const [apiKey, setApiKey] = useState("");
  const [keySaved, setKeySaved] = useState(false);
  const [counts, setCounts] = useState<Record<string, number | null> | null>(null);
  const [countsAt, setCountsAt] = useState<number | null>(null);
  const [lastSyncAt, setLastSyncAt] = useState<number | null>(null);
  const [include, setInclude] = useState<Record<ImportKey, boolean>>({
    customers: true,
    items: true,
    jobs: true,
    estimates: true,
    invoices: true,
    team: false,
  });
  // entity → picked FP ids (absent = all records)
  const [selections, setSelections] = useState<Partial<Record<ImportKey, number[]>>>({});
  const [picker, setPicker] = useState<ImportKey | null>(null);
  const [jobs, setJobs] = useState<ImportJob[]>([]);
  const [busy, setBusy] = useState<"save" | "test" | "start" | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!tenantId) return;
    const subs = [
      onSnapshot(
        query(collection(db, paths.tenantImportJobs(tenantId)), orderBy("startedAt", "desc")),
        (snap) => setJobs(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as ImportJob)),
      ),
      // Counts + last-migrated persist on the integration config.
      onSnapshot(doc(db, paths.tenantIntegrationConfig(tenantId, "fieldpulse-import")), (snap) => {
        setCounts((snap.get("fpCounts") as Record<string, number | null>) ?? null);
        setCountsAt((snap.get("fpCountsAt") as number) ?? null);
        setLastSyncAt((snap.get("lastSyncAt") as number) ?? null);
        setKeySaved(Boolean(snap.get("credentialsEncrypted")));
      }),
    ];
    return () => subs.forEach((u) => u());
  }, [tenantId]);

  const running = jobs.find((j) => j.status === "running");
  const completed = jobs.find((j) => j.status === "done");

  async function saveKey() {
    if (!tenantId) return;
    setBusy("save");
    setError("");
    try {
      await call("upsertIntegrationCredentials", {
        tenantId,
        slug: "fieldpulse-import",
        credentials: { apiKey: apiKey.trim() },
      });
      setKeySaved(true);
      setApiKey("");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function test() {
    if (!tenantId) return;
    setBusy("test");
    setError("");
    try {
      await call("testFieldPulseConnection", { tenantId }, { mutation: false });
      // the config snapshot delivers the fresh counts
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function start() {
    if (!tenantId) return;
    setBusy("start");
    setError("");
    try {
      const picks: Record<string, number[]> = {};
      for (const k of IMPORT_KEYS) {
        const ids = selections[k];
        if (include[k] && ids && ids.length > 0) picks[k] = ids;
      }
      // Long-running: the progress card below tracks it live.
      void call(
        "startFieldPulseImport",
        { tenantId, include, ...(Object.keys(picks).length ? { selections: picks } : {}) },
        { mutation: false },
      ).catch((err) => setError((err as Error).message));
    } finally {
      setBusy(null);
    }
  }

  const totalFor = (k: ImportKey): number | null => counts?.[LIST_ENTITY[k]] ?? null;

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <div>
        <a href="/app/migration" className="text-xs text-steel hover:text-ink">
          ← Migration
        </a>
        <h1 className="text-lg font-bold">FieldPulse Migration</h1>
        {lastSyncAt ? (
          <p className="text-xs text-success">Last migrated {fmtWhen(lastSyncAt)}</p>
        ) : null}
      </div>
      <p className="text-xs text-steel">
        Bring your FieldPulse data into Clevis: connect with your API key, see what's there, choose
        what to move. FieldPulse is never modified — this only reads.
      </p>
      {error ? <p className="text-xs text-danger">{error}</p> : null}

      {completed && !running ? (
        <Card className="border-success/50">
          <CardTitle className="text-success">
            {completed.incremental ? "Last sync" : "First import complete"}
          </CardTitle>
          {Object.entries(completed.progress ?? {}).map(([k, v]) => (
            <p key={k} className="text-sm text-ink">
              {ENTITY_LABELS[k] ?? k}: {v.imported.toLocaleString()} imported
              {v.skipped ? (
                <span className="text-xs text-steel"> · {v.skipped.toLocaleString()} unchanged</span>
              ) : null}
            </p>
          ))}
          <p className="mt-2 text-xs text-steel">
            Still updating FieldPulse while you learn Clevis? Sync again below any time — records that
            came over before are updated in place, never duplicated.
          </p>
        </Card>
      ) : null}
      {completed && !running && tenantId ? <BuilderCleanupSuggestion tenantId={tenantId} /> : null}

      {running ? (
        <Card className="border-brandorange/50">
          <CardTitle>Importing…</CardTitle>
          {Object.entries(running.progress ?? {}).map(([k, v]) => (
            <p key={k} className="text-sm text-ink">
              {ENTITY_LABELS[k] ?? k}: {v.imported.toLocaleString()} so far
            </p>
          ))}
          <p className="mt-2 text-xs text-steel">
            Safe to leave this page — the import continues on our servers.
          </p>
        </Card>
      ) : null}

      {!running ? (
        <>
          <Card>
            <CardTitle>1 · Connect</CardTitle>
            <p className="mb-2 text-xs text-steel">
              Paste your FieldPulse API key (FieldPulse support can provide it). It's stored encrypted.
            </p>
            <div className="flex gap-2">
              <Input
                type="password"
                placeholder={keySaved ? "Key saved ✓ — paste to replace" : "FieldPulse API key"}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
              />
              <Button onClick={() => void saveKey()} busy={busy === "save"} disabled={apiKey.trim().length < 10}>
                Save key
              </Button>
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <CardTitle className="mb-0">2 · What's in FieldPulse</CardTitle>
              <div className="flex items-center gap-2">
                {countsAt ? <span className="text-[11px] text-steel">Counted {fmtWhen(countsAt)}</span> : null}
                <Button onClick={() => void test()} busy={busy === "test"} variant="secondary" className="px-3 py-1 text-xs">
                  {counts ? "Re-count" : "Test connection & count"}
                </Button>
              </div>
            </div>
            {counts ? (
              <>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {IMPORT_KEYS.map((k) => {
                    const total = totalFor(k);
                    const picked = selections[k];
                    return (
                      <button
                        key={k}
                        className={cn(
                          "rounded-lg bg-cream px-3 py-2 text-center transition-colors hover:ring-2 hover:ring-brandorange/60",
                          picked ? "ring-2 ring-brandorange" : "",
                        )}
                        onClick={() => setPicker(k)}
                        title="Choose which records to bring over"
                      >
                        <div className="text-lg font-bold text-ink">
                          {picked
                            ? `${picked.length.toLocaleString()} of ${(total ?? 0).toLocaleString()}`
                            : total === null
                              ? "—"
                              : total.toLocaleString()}
                        </div>
                        <div className="text-[11px] text-steel">
                          {ENTITY_LABELS[LIST_ENTITY[k]]}
                          {picked ? " · selected" : ""}
                        </div>
                      </button>
                    );
                  })}
                </div>
                <p className="mt-2 text-[11px] text-steel">
                  Tap a card to hand-pick records; untouched cards bring everything.
                </p>
              </>
            ) : (
              <p className="mt-2 text-xs text-steel">Run the count to see what's waiting in FieldPulse.</p>
            )}
          </Card>

          <Card>
            <CardTitle>3 · Choose & import</CardTitle>
            <div className="mb-3 space-y-1">
              {IMPORT_KEYS.map((key) => (
                <label key={key} className="flex items-center gap-2 text-sm text-ink">
                  <input
                    type="checkbox"
                    checked={include[key]}
                    onChange={(e) => setInclude({ ...include, [key]: e.target.checked })}
                  />
                  {KEY_LABEL[key]}
                  {include[key] && selections[key] ? (
                    <span className="text-xs text-brandorange-dark">
                      ({selections[key]!.length.toLocaleString()} picked)
                    </span>
                  ) : null}
                </label>
              ))}
            </div>
            <Button
              onClick={() => void start()}
              busy={busy === "start"}
              disabled={(!counts && !completed) || !Object.values(include).some(Boolean)}
            >
              {completed ? "Sync again from FieldPulse" : "Start import"}
            </Button>
            {!counts && !completed ? (
              <p className="mt-1 text-[11px] text-steel">Run the count first.</p>
            ) : null}
            {completed ? (
              <p className="mt-1 text-[11px] text-steel">
                Re-syncs only touch what changed in FieldPulse since last time — anything you've already
                edited in Clevis keeps your Clevis-only changes (assignments, photos, signatures).
              </p>
            ) : null}
          </Card>
        </>
      ) : null}

      {picker && tenantId ? (
        <RecordPicker
          tenantId={tenantId}
          entity={picker}
          total={totalFor(picker)}
          initial={selections[picker] ?? null}
          onDone={(ids) => {
            setSelections((prev) => {
              const next = { ...prev };
              if (ids === null) delete next[picker];
              else next[picker] = ids;
              return next;
            });
            setPicker(null);
          }}
        />
      ) : null}
    </div>
  );
}

// The record-picking popup: loads FieldPulse records in chunks, filter as you
// type, check what you want. Leaving everything checked (or checking nothing)
// means "bring everything".
function RecordPicker({
  tenantId,
  entity,
  total,
  initial,
  onDone,
}: {
  tenantId: string;
  entity: ImportKey;
  total: number | null;
  initial: number[] | null;
  onDone: (ids: number[] | null) => void;
}) {
  const [rows, setRows] = useState<PickRow[]>([]);
  const [nextPage, setNextPage] = useState<number | null>(1);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [checked, setChecked] = useState<Set<number>>(() => new Set(initial ?? []));
  const [touched, setTouched] = useState(initial !== null);
  const [error, setError] = useState("");

  async function loadMore() {
    if (nextPage === null || loading) return;
    setLoading(true);
    setError("");
    try {
      const res = await call<{ rows: PickRow[]; nextPage: number | null }>(
        "listFieldPulseRecords",
        { tenantId, entity: LIST_ENTITY[entity], startPage: nextPage, pages: 10 },
        { mutation: false },
      );
      setRows((prev) => [...prev, ...res.rows]);
      setNextPage(res.nextPage);
      if (!touched) {
        // Everything starts checked — unchecking is how you exclude.
        setChecked((prev) => {
          const next = new Set(prev);
          res.rows.forEach((r) => next.add(r.id));
          return next;
        });
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visible = useMemo(() => {
    const term = filter.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) => r.label.toLowerCase().includes(term) || r.sub.toLowerCase().includes(term));
  }, [rows, filter]);

  function toggle(id: number) {
    setTouched(true);
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function setAll(on: boolean) {
    setTouched(true);
    setChecked(() => (on ? new Set(rows.map((r) => r.id)) : new Set()));
  }

  function done() {
    // Nothing excluded (or nothing loaded) → no selection = bring everything.
    const allLoadedChecked = rows.length > 0 && rows.every((r) => checked.has(r.id));
    if (!touched || (allLoadedChecked && nextPage === null)) onDone(null);
    else onDone([...checked]);
  }

  const totalLabel = total ?? rows.length;
  return (
    <div className="fixed inset-0 z-30 flex items-end justify-center bg-ink/30 p-4 md:items-center" onClick={done}>
      <div
        className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-xl bg-white p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-bold text-ink">
            Pick {(ENTITY_LABELS[LIST_ENTITY[entity]] ?? entity).toLowerCase()}
          </h2>
          <span className="text-xs text-steel">
            {checked.size.toLocaleString()} of {Number(totalLabel).toLocaleString()} selected
          </span>
        </div>
        <div className="mb-2 flex items-center gap-2">
          <Input
            placeholder="Filter loaded records…"
            className="py-1.5 text-sm"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <button className="shrink-0 text-xs text-steel hover:text-ink" onClick={() => setAll(true)}>
            All
          </button>
          <button className="shrink-0 text-xs text-steel hover:text-ink" onClick={() => setAll(false)}>
            None
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-steel/15">
          {visible.map((r) => (
            <label
              key={r.id}
              className="flex cursor-pointer items-center gap-2 border-b border-steel/10 px-3 py-1.5 text-sm last:border-0 hover:bg-cream/60"
            >
              <input type="checkbox" checked={checked.has(r.id)} onChange={() => toggle(r.id)} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-ink">{r.label}</span>
                {r.sub ? <span className="block truncate text-[11px] text-steel">{r.sub}</span> : null}
              </span>
            </label>
          ))}
          {visible.length === 0 && !loading ? (
            <p className="px-3 py-6 text-center text-xs text-steel">
              {filter ? "No matches among loaded records." : "Nothing here."}
            </p>
          ) : null}
        </div>
        {error ? <p className="mt-1 text-xs text-danger">{error}</p> : null}
        <div className="mt-2 flex items-center justify-between">
          {nextPage !== null ? (
            <Button variant="ghost" className="px-3 py-1 text-xs" busy={loading} onClick={() => void loadMore()}>
              Load more ({rows.length.toLocaleString()} of {Number(totalLabel).toLocaleString()} loaded)
            </Button>
          ) : (
            <span className="text-[11px] text-steel">All {rows.length.toLocaleString()} loaded</span>
          )}
          <Button className="px-4 py-1.5 text-sm" onClick={done}>
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}

// After an import: FieldPulse companies often kept one customer per
// development ("KB Homes" ×37). Offer the cleanup with the real numbers.
function BuilderCleanupSuggestion({ tenantId }: { tenantId: string }) {
  const [found, setFound] = useState<{ builders: number; records: number; top: string } | null>(null);
  useEffect(() => {
    void call<{ groups: Array<{ builderName: string; children: unknown[] }> }>("planBuilderCleanup", { tenantId }, { mutation: false })
      .then((res) => {
        if (res.groups.length === 0) return;
        const top = res.groups[0]!;
        setFound({
          builders: res.groups.length,
          records: res.groups.reduce((n, g) => n + g.children.length, 0),
          top: `${top.builderName} ×${top.children.length + 1}`,
        });
      })
      .catch(() => undefined);
  }, [tenantId]);
  if (!found) return null;
  return (
    <Card className="border-brandorange/50">
      <CardTitle>Next: one customer per builder</CardTitle>
      <p className="text-sm text-ink">
        FieldPulse made a separate customer for each development: {found.builders.toLocaleString()} builders have{" "}
        {found.records.toLocaleString()} extra customer records (biggest: {found.top}). Clevis can fold each builder
        into one customer with a project per development, and move their jobs, estimates and invoices.
      </p>
      <Link href="/migration/builders" className="mt-2 inline-block text-sm font-semibold text-brandorange-dark underline">
        Review the cleanup →
      </Link>
    </Card>
  );
}
